import { useState, useEffect, useContext } from 'react';
import { Link, Box, Typography, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import { sporttiaColors } from '../../../theme/theme';
import { format, isPast, parseISO } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import SttCachedTable from '../../../sporttia/SttCachedTable';
import {
    AppContext,
    SttButtonFab,
    SttChipSport,
    SttPage,
} from '../../../sporttia/all';
import { SttHasDeviceIcon } from '../../../sporttia/icons/SttHasDeviceIcon';
import { SPORTCENTER_ACTIVITIES } from '../../../lib/queryKeys';
import useScsService from '../../../services/ScsService';
import { useCrud, useQueryFilters } from '../../../lib/hooks';
import Activity from './Activity';
import { fullName } from '../../../lib/utils';
import { SttTopFilteringControls } from '../../../sporttia/SttTopFilteringControls';
import { SttActivitiesDuplicationDialog } from '../../../sporttia/dialogs/SttActivitiesDuplicationDialog';
import DateBuilder from '../../../lib/DateBuilder';
import { PERMISSIONS } from '../../../lib/permissions';

type Tag = {
    id: number;
    name: string;
};

type ActivityState = {
    id: number | string | null;
    trash: boolean;
};

type Filters = {
    name: string;
    year: string;
    type: string;
    idSport: string;
    status: string;
    tags: string;
    trash: boolean;
    idActivity?: string;
};

/**
 * 'ACTIVE' and 'ALL' are filter options, not activity statuses: an active
 * activity is one whose period includes today, whether open or closed.
 */
function toActivitiesParams({
    status,
    ...params
}: Record<string, unknown>): Record<string, unknown> {
    if (status === 'ACTIVE') {
        return { ...params, inTime: true };
    }
    if (status === 'ALL') {
        return params;
    }
    return { ...params, status };
}

export default function Activities(): React.ReactElement | null {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { id: urlActivityId, tab: urlTabKey } = useParams<{
        id?: string;
        tab?: string;
    }>();

    const [scTags, setScTags] = useState<Tag[]>();
    const [activity, setActivity] = useState<ActivityState>(() => ({
        id: urlActivityId ? parseInt(urlActivityId, 10) || urlActivityId : null,
        trash: false,
    }));
    const scsService = useScsService();
    const [filters, setFilters] = useQueryFilters<Filters>(
        {
            name: '',
            year: format(new Date(), 'yyyy'),
            type: '',
            idSport: '',
            status: 'ACTIVE',
            tags: '',
            trash: false,
        },
        SPORTCENTER_ACTIVITIES,
    );
    const [selectRows, setSelectRows] = useState<unknown[]>([]);
    const [activitiesToDuplicate, setActivitiesToDuplicate] = useState<
        number[]
    >([]);
    const [, , , Get] = useCrud<any>();

    const cxt = useContext(AppContext)!;

    const getSportCenterTags = (): void => {
        if (cxt?.sc?.id) {
            Get<{ rows?: Tag[] }>(`/scs/${cxt.sc?.id}/tags`).then(
                (response) => {
                    setScTags(response?.rows);
                },
            );
        }
    };

    // Init
    useEffect(() => {
        getSportCenterTags();
    }, []);

    // Handle URL parameter changes
    useEffect(() => {
        if (urlActivityId) {
            const parsedId = parseInt(urlActivityId, 10);
            setActivity({
                id: !Number.isNaN(parsedId) ? parsedId : urlActivityId,
                trash: false,
            });
        }
    }, [urlActivityId]);

    // Cargando...
    if (!scTags) {
        return null;
    }

    return (
        <SttPage title={t('Activities')}>
            <SttTopFilteringControls
                p={2}
                fields={[
                    {
                        caption: t('Name'),
                        name: 'name',
                        type: 'text',
                        value: filters.name,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 3,
                    },
                ]}
                extraFields={[
                    {
                        caption: t('Code'),
                        name: 'idActivity',
                        type: 'text',
                        value: filters.idActivity,
                    },
                    {
                        caption: t('Year'),
                        type: 'year',
                        name: 'year',
                        years: {
                            future: 1,
                        },
                        value: filters.year || '',
                    },
                    {
                        caption: t('Type'),
                        type: 'select',
                        name: 'type',
                        value: filters.type,
                        options: [
                            {
                                caption: t(`CT.COURSE`),
                                value: 'COURSE',
                            },
                            {
                                caption: t(`CT.PUNCTUAL`),
                                value: 'PUNCTUAL',
                            },
                        ],
                    },
                    {
                        caption: t('Sport'),
                        type: 'sportDialog',
                        name: 'idSport',
                        value: filters.idSport,
                    },
                    {
                        caption: t('Status'),
                        type: 'select',
                        name: 'status',
                        value: filters.status,
                        options: [
                            {
                                caption: t('All'),
                                value: 'ALL',
                            },
                            {
                                caption: t('Active'),
                                value: 'ACTIVE',
                            },
                            {
                                caption: t(`CS.OPEN`),
                                value: 'OPEN',
                            },
                            {
                                caption: t(`CS.CLOSE`),
                                value: 'CLOSE',
                            },
                        ],
                    },
                    {
                        caption: t('Tags'),
                        name: 'tags',
                        type: 'selectMulti',
                        value: filters.tags,
                        xs: 12,
                        sm: 6,
                        md: 4,
                        lg: 9,
                        options: scTags.map((tag: any) => ({
                            label: tag.name,
                            value: tag.id,
                        })),
                    },
                ]}
                onFilter={(
                    value: { year?: string | number } & Partial<Filters>,
                ) => {
                    let year = null;
                    if (value?.year) {
                        year = value.year.toString();
                    }
                    setFilters({ ...value, year } as Filters);
                }}
                trashAction
            />

            <SttCachedTable
                queryKey={SPORTCENTER_ACTIVITIES}
                queryFn={(params: Record<string, unknown>) =>
                    scsService.getActivities(
                        cxt?.sc?.id ?? 0,
                        toActivitiesParams(params),
                    )
                }
                queryParams={filters}
                prefetching={false}
                setSelectRows={setSelectRows}
                columns={[
                    {
                        title: '',
                        width: 40,
                        align: 'center',
                        value: (row: { type: string }) => (
                            <Tooltip
                                title={t(
                                    row.type === 'COURSE'
                                        ? 'CT.COURSE'
                                        : 'CT.PUNCTUAL',
                                )}
                            >
                                {row.type === 'COURSE' ? (
                                    <SchoolIcon
                                        fontSize="small"
                                        color="primary"
                                    />
                                ) : (
                                    <EmojiEventsIcon
                                        fontSize="small"
                                        color="secondary"
                                    />
                                )}
                            </Tooltip>
                        ),
                    },
                    {
                        title: t('Name'),
                        align: 'left',
                        width: 250,
                        value: (row: {
                            id: number;
                            trash: boolean;
                            status: string;
                            user?: { name?: string; surname?: string };
                            name: string;
                            numClasses: number;
                        }) => (
                            <div>
                                <Link
                                    component="button"
                                    onClick={() =>
                                        setActivity({
                                            id: row.id,
                                            trash: row.trash,
                                        })
                                    }
                                    sx={{
                                        color:
                                            row.status === 'CLOSE'
                                                ? 'error.main'
                                                : 'var(--brand-text)',
                                        textDecoration: 'none',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            textDecoration: 'underline',
                                        },
                                    }}
                                >
                                    {row.user ? fullName(row.user) : row.name}
                                </Link>
                                {row.numClasses > 0 && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {`${row.numClasses} ${t('Classes')}`}
                                    </Typography>
                                )}
                            </div>
                        ),
                    },
                    {
                        title: t('Sport'),
                        value: (row: {
                            sport?: { id: number; name: string };
                        }) =>
                            row.sport ? (
                                <SttChipSport sport={row.sport} />
                            ) : null,
                    },
                    {
                        title: t('Assigned'),
                        align: 'center',
                        width: 120,
                        value: (row: {
                            teacher?: { name: string };
                            hasDevice?: boolean;
                        }) => {
                            const hasTeacher = !!row.teacher;
                            return (
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Tooltip
                                        title={
                                            hasTeacher
                                                ? row.teacher?.name ||
                                                  t('Teacher')
                                                : t('activity.noTeacher')
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 36,
                                                height: 36,
                                                borderRadius: '50%',
                                                backgroundColor: hasTeacher
                                                    ? alpha(
                                                          sporttiaColors.info
                                                              .main,
                                                          0.15,
                                                      )
                                                    : sporttiaColors.icon
                                                          .disabledBg,
                                            }}
                                        >
                                            <PersonIcon
                                                sx={{
                                                    color: hasTeacher
                                                        ? 'info.main'
                                                        : sporttiaColors.icon
                                                              .disabled,
                                                    fontSize: 20,
                                                }}
                                            />
                                        </Box>
                                    </Tooltip>
                                    <SttHasDeviceIcon
                                        hasDevice={row.hasDevice}
                                    />
                                </Box>
                            );
                        },
                    },
                    {
                        title: `${t('Inscription')} / ${t('Period')}`,
                        value: (row: {
                            inscIni: string;
                            inscEnd: string;
                            ini: string;
                            end: string;
                        }) => {
                            const dateBuilder = (date: string) =>
                                new DateBuilder(
                                    date,
                                    cxt?.sc?.timezone?.name,
                                    cxt?.lang?.key,
                                ).dmy();
                            const isInscriptionPast = isPast(
                                parseISO(row.inscEnd),
                            );
                            const isPeriodPast = isPast(parseISO(row.end));
                            return (
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color={
                                            isInscriptionPast
                                                ? 'error.main'
                                                : 'success.main'
                                        }
                                    >
                                        {dateBuilder(row.inscIni)} -{' '}
                                        {dateBuilder(row.inscEnd)}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={
                                            isPeriodPast
                                                ? 'error.main'
                                                : 'success.main'
                                        }
                                    >
                                        {dateBuilder(row.ini)} -{' '}
                                        {dateBuilder(row.end)}
                                    </Typography>
                                </Box>
                            );
                        },
                    },
                    {
                        type: 'counter',
                        title: t('Students'),
                        value: (row: {
                            numStudents: number;
                            maxStudents: number | null;
                        }) => `${row.numStudents} / ${row.maxStudents || '∞'}`,
                    },
                    {
                        type: 'price',
                        title: t('Total'),
                        field: 'total',
                    },
                    {
                        type: 'debt',
                        title: t('Debt'),
                        value: (row: { debt?: number }) => ({
                            price: row?.debt,
                        }),
                    },
                ]}
                selection={{
                    actions: [
                        {
                            caption: t('duplicateCourses'),
                            onClick: () => {
                                setActivitiesToDuplicate(
                                    (selectRows as Array<{ id?: number }>)
                                        .map(
                                            (activitySelected) =>
                                                activitySelected?.id,
                                        )
                                        .filter(
                                            (id): id is number =>
                                                id !== undefined,
                                        ),
                                );
                            },
                        },
                    ],
                }}
            />
            <Activity
                id={activity.id}
                trash={activity.trash}
                initialTabKey={urlTabKey}
                // apply changes locally, remove items with 'trash'
                onChange={(newActivity?: ActivityState) => {
                    if (newActivity) {
                        setActivity(newActivity);
                    }
                    queryClient.invalidateQueries({
                        queryKey: [SPORTCENTER_ACTIVITIES],
                    });
                }}
                onClose={() => {
                    setActivity({ id: null, trash: false });
                    navigate('/activities');
                }}
            />
            <SttButtonFab
                onClick={() => setActivity({ id: 'create', trash: false })}
                permission={PERMISSIONS.ACTIVITY.CREATE}
            />

            {activitiesToDuplicate?.length > 0 && (
                <SttActivitiesDuplicationDialog
                    open
                    activities={activitiesToDuplicate}
                    onClose={() => setActivitiesToDuplicate([])}
                    onDuplicate={() => {
                        setActivitiesToDuplicate([]);
                        // The copies have to show up in the list.
                        queryClient.invalidateQueries({
                            queryKey: [SPORTCENTER_ACTIVITIES],
                        });
                    }}
                />
            )}
        </SttPage>
    );
}
